import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, FIG_LISTS } from '../../api/figApi';
import FIGInsured from './FIGInsured';
import FIGVault from './FIGVault';
import AcordLibrary from './AcordLibrary';
import FIGRenewalPipeline from './FIGRenewalPipeline';
import ComposeLetter from './ComposeLetter';
import './FIG.css';

function FIGDashboard({ getToken, onBack }) {
  const [insureds, setInsureds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedInsured, setSelectedInsured] = useState(null);
  const [insuredOpts, setInsuredOpts] = useState({});
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'clients' | 'vault' | 'letters'
  const [vaultInitialPath, setVaultInitialPath] = useState(null);
  const [vaultToast, setVaultToast] = useState(null);

  useEffect(() => {
    if (!vaultToast) return undefined;
    const t = setTimeout(() => setVaultToast(null), 3500);
    return () => clearTimeout(t);
  }, [vaultToast]);
  const [dashboardClientId, setDashboardClientId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add form state
  const [newCompany, setNewCompany] = useState('');
  const [newFEIN, setNewFEIN] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const fetchInsureds = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const items = await getItems(token, FIG_LISTS.INSUREDS);
      setInsureds(items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchInsureds();
  }, [fetchInsureds]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newCompany.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await createItem(token, FIG_LISTS.INSUREDS, {
        Title: newCompany.trim(),
        CompanyName: newCompany.trim(),
        FEIN: newFEIN.trim(),
        PrimaryFirstName: newFirstName.trim(),
        PrimaryLastName: newLastName.trim(),
        PrimaryEmail: newEmail.trim(),
        PrimaryPhone: newPhone.trim(),
        Status: 'Active',
      });
      setNewCompany('');
      setNewFEIN('');
      setNewFirstName('');
      setNewLastName('');
      setNewEmail('');
      setNewPhone('');
      setShowAddForm(false);
      await fetchInsureds();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const filtered = insureds.filter(ins => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (ins.CompanyName || '').toLowerCase().includes(q) ||
      (ins.Principal || '').toLowerCase().includes(q) ||
      ((ins.PrimaryFirstName || '') + ' ' + (ins.PrimaryLastName || '')).toLowerCase().includes(q) ||
      (ins.FEIN || '').toLowerCase().includes(q)
    );
  });

  if (selectedInsured) {
    return (
      <FIGInsured
        insured={selectedInsured}
        getToken={getToken}
        onBack={() => { setSelectedInsured(null); setInsuredOpts({}); }}
        initialTab={insuredOpts.tab}
        initialPolicyId={insuredOpts.policyId}
        initialPolicy={insuredOpts.policy}
      />
    );
  }

  const tabBtn = (key, label) => {
    const active = view === key;
    return (
      <button
        key={key}
        onClick={() => { setView(key); if (key !== 'vault') setVaultInitialPath(null); }}
        style={{
          padding: '0.5rem 1.1rem', border: 'none', cursor: 'pointer',
          background: active ? '#4B0082' : '#faf7ee',
          color: active ? '#fff' : '#4B0082',
          fontWeight: 700, fontSize: '0.85rem',
          borderTopLeftRadius: '6px', borderTopRightRadius: '6px',
          borderBottom: active ? '2px solid #4B0082' : '2px solid transparent',
          marginBottom: '-2px',
        }}>
        {label}
      </button>
    );
  };

  return (
    <div className="FIG">
      {vaultToast && (
        <div
          onClick={() => setVaultToast(null)}
          style={{
            position: 'fixed', top: '33%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9000,
            padding: '12px 18px 12px 14px',
            background: 'rgba(18, 14, 10, 0.96)',
            border: '1px solid rgba(212, 170, 62, 0.55)',
            borderLeft: '3px solid #d4aa3e',
            borderRadius: '3px',
            fontFamily: "'Share Tech Mono', 'SF Mono', 'Consolas', monospace",
            fontSize: '0.82rem', letterSpacing: '0.6px',
            color: '#e6e0d3',
            boxShadow: '0 6px 22px rgba(0, 0, 0, 0.55), 0 0 14px rgba(212, 170, 62, 0.25)',
            display: 'flex', alignItems: 'center', gap: '10px',
            cursor: 'pointer',
            maxWidth: '460px',
            animation: 'FIG-vault-toast-in 180ms ease-out',
          }}
          title="Click to dismiss"
          role="status"
          aria-live="polite"
        >
          <span style={{ color: '#d4aa3e', fontWeight: 700, letterSpacing: 0 }}>&#9650;</span>
          <span style={{ flex: 1 }}>{vaultToast}</span>
        </div>
      )}
      <div className="FIG-top-bar">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; CENTCOM</button>
        <h1 className="FIG-title">FIG Insurance</h1>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '2px', borderBottom: '2px solid #d8d1bf',
        margin: '0.3rem 0 0.9rem' }}>
        {tabBtn('dashboard', 'Dashboard')}
        {tabBtn('clients', 'Clients')}
        {tabBtn('letters', 'Letters')}
        {tabBtn('vault', 'Vault')}
        {tabBtn('acord', 'ACORD Library')}
      </div>

      {view === 'dashboard' ? (
        <FIGRenewalPipeline
          getToken={getToken}
          selectedClientId={dashboardClientId}
          onSelectedClientChange={setDashboardClientId}
          onSelectInsured={(ins, opts) => {
            if (opts?.tab === 'Vault' && opts.vaultPath) {
              setVaultInitialPath(opts.vaultPath);
              if (opts.vaultToast) setVaultToast(opts.vaultToast);
              setView('vault');
              return;
            }
            setInsuredOpts(opts || {}); setSelectedInsured(ins);
          }}
        />
      ) : view === 'vault' ? (
        <FIGVault getToken={getToken} initialPath={vaultInitialPath} key={vaultInitialPath || 'vault'} />
      ) : view === 'acord' ? (
        <AcordLibrary getToken={getToken} />
      ) : view === 'letters' ? (
        <ComposeLetter getToken={getToken} insureds={insureds} />
      ) : (
      <div className="FIG-clients-pane">
      {error && <p className="FIG-error">{error}</p>}

      <div className="FIG-controls">
        <input
          className="FIG-search"
          type="text"
          placeholder="Search by company, contact, principal, or FEIN..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          className="FIG-add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Insured'}
        </button>
      </div>

      {showAddForm && (
        <form className="FIG-add-form" onSubmit={handleAdd}>
          <input
            className="FIG-input"
            type="text"
            placeholder="Company Name *"
            value={newCompany}
            onChange={e => setNewCompany(e.target.value)}
            required
            autoFocus
          />
          <input
            className="FIG-input"
            type="text"
            placeholder="FEIN"
            value={newFEIN}
            onChange={e => setNewFEIN(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              className="FIG-input"
              type="text"
              placeholder="First Name"
              value={newFirstName}
              onChange={e => setNewFirstName(e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              className="FIG-input"
              type="text"
              placeholder="Last Name"
              value={newLastName}
              onChange={e => setNewLastName(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          <input
            className="FIG-input"
            type="email"
            placeholder="Email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
          />
          <input
            className="FIG-input"
            type="tel"
            placeholder="Phone"
            value={newPhone}
            onChange={e => setNewPhone(e.target.value)}
          />
          <button className="FIG-save-btn" type="submit" disabled={saving || !newCompany.trim()}>
            {saving ? 'Saving...' : 'Save Insured'}
          </button>
        </form>
      )}

      {loading && <p className="FIG-loading">Loading insureds...</p>}

      {!loading && filtered.length === 0 && (
        <p className="FIG-empty">{search ? 'No matching insureds.' : 'No insureds yet. Add one above.'}</p>
      )}

      <div className="FIG-card-list">
        {filtered.map(ins => (
          <button
            key={ins.id}
            className="FIG-card"
            onClick={() => setSelectedInsured(ins)}
          >
            <div className="FIG-card-header">
              <span className="FIG-card-name">{ins.CompanyName || ins.Title || 'Unnamed'}</span>
              <span className={`FIG-card-status FIG-card-status--${(ins.Status || 'Active').toLowerCase()}`}>
                {ins.Status || 'Active'}
              </span>
            </div>
            {ins.Principal && <span className="FIG-card-principal">Principal: {ins.Principal}</span>}
            {(ins.PrimaryFirstName || ins.PrimaryLastName) && <span className="FIG-card-contact">{[ins.PrimaryFirstName, ins.PrimaryLastName].filter(Boolean).join(' ')}</span>}
          </button>
        ))}
      </div>
      </div>
      )}
    </div>
  );
}

export default FIGDashboard;
